import * as packagesService from "./services/packagesService";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getStatusBgColor, getStatusTextColor } from "@shared/utils/statusColors";
import { 
  STATUS_LIST, 
  STATUS_COLORS, 
  STATUS_OPTIONS,
  CHART_STATUS_COLORS 
} from "@shared/utils/constants";

import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import {
  ChevronDown,
  ChevronUp,
  Package,
  Search,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Alert, AlertDescription } from "@shared/components/ui/alert";

// Status mapping configuration for packages tracking
const PACKAGE_STATUS_CONFIG = {
  progressing: {
    status: STATUS_LIST[0], // "order_received" - Use constants from STATUS_LIST
    defaultNote: "Package tracking information received",
  },
  STATUS_MAPPING: {
    Collected: {
      status: STATUS_LIST[10], // "signed" - Using STATUS_LIST values
      priority: 5,
    },
    "Return Collection": {
      status: STATUS_LIST[12], // "return_collection" - Using STATUS_LIST values
      priority: 6,
    },
    "Site Departure": {
      status: STATUS_LIST[4], // "in_transit" - Using STATUS_LIST values
      priority: 3,
    },
    "In Transit": {
      status: STATUS_LIST[4], // "in_transit" - Using STATUS_LIST values
      priority: 2,
    },
    "Out for Delivery": {
      status: STATUS_LIST[9], // "delivering" - Using STATUS_LIST values
      priority: 4,
    },
    "Failed Attempt": {
      status: STATUS_LIST[9], // "delivering" - Using STATUS_LIST values
      priority: 4,
    },
    "Arrived at Facility": {
      status: STATUS_LIST[5], // "arrived_at_warehouse" - Using STATUS_LIST values
      priority: 2,
    },
    "Picked Up": {
      status: STATUS_LIST[3], // "picked_up" - Using STATUS_LIST values
      priority: 1,
    },
    "Recipient Request Reschedule": {
      status: STATUS_LIST[11], // "abnormal_signed" - Using STATUS_LIST values
      priority: 4,
    },
    "Recipient'S Phone Cannot Be Reached": {
      status: STATUS_LIST[11], // "abnormal_signed" - Using STATUS_LIST values
      priority: 4,
    },
  },
};

// Utility function to generate status timeline
const generateStatusTimeline = (tracks) => {
  if (!tracks || tracks.length === 0) {
    return [{
      status: PACKAGE_STATUS_CONFIG.progressing.status,
      note: PACKAGE_STATUS_CONFIG.progressing.defaultNote,
      user: "speedaf",
      date: new Date().toISOString(),
      time: new Date().toISOString(),
      actionName: "Package Received"
    }];
  }

  const statusMap = new Map();
  const sortedTracks = [...tracks].sort(
    (a, b) => new Date(a.time) - new Date(b.time)
  );

  // Initialize with progressing status
  statusMap.set(PACKAGE_STATUS_CONFIG.progressing.status, {
    status: PACKAGE_STATUS_CONFIG.progressing.status,
    note: PACKAGE_STATUS_CONFIG.progressing.defaultNote,
    user: "speedaf",
    date: new Date(sortedTracks[0].time).toISOString(),
    time: sortedTracks[0].time,
    actionName: "Package Received"
  });

  for (const track of sortedTracks) {
    const mappedStatus = PACKAGE_STATUS_CONFIG.STATUS_MAPPING[track.actionName];

    if (mappedStatus) {
      const { status, priority } = mappedStatus;
      const note = track.msgFre || track.msgEng || track.msgArab || track.actionName;

      const existingStatus = statusMap.get(status);
      if (!existingStatus || new Date(track.time) > new Date(existingStatus.time)) {
        statusMap.set(status, {
          ...track,
          status,
          note,
          user: track.user || "speedaf",
          date: new Date(track.time).toISOString(),
          priority,
        });
      }
    } else {
      // Handle unknown status as order_received (first status in STATUS_LIST)
      const note = track.msgFre || track.msgEng || track.msgArab || track.actionName;
      statusMap.set(STATUS_LIST[0], {
        ...track,
        status: STATUS_LIST[0], // "order_received"
        note,
        user: track.user || "speedaf",
        date: new Date(track.time).toISOString(),
        priority: 1,
      });
    }
  }

  return Array.from(statusMap.values()).sort((a, b) => new Date(b.time) - new Date(a.time));
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();

  // Use STATUS_COLORS from constants for consistent styling
  const statusClass = STATUS_COLORS[status] || "bg-gray-100 text-gray-600 border-gray-200";

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium border ${statusClass}`}
    >
      {t(STATUS_OPTIONS[status] || `status.${status}`)}
    </span>
  );
};

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const TrackingEvent = ({ event, isLast, isFirst, customerInfo }) => {
  const { t } = useTranslation();
  
  const getStatusIcon = (status) => {
    // Use STATUS_LIST constants for consistent status handling
    switch (status) {
      case STATUS_LIST[0]: // "order_received"
        return "bg-blue-500";
      case STATUS_LIST[1]: // "order_processed"
        return "bg-indigo-500";
      case STATUS_LIST[2]: // "progressing"
        return "bg-purple-500";
      case STATUS_LIST[3]: // "shipped"
        return "bg-violet-500";
      case STATUS_LIST[4]: // "in_transit"
        return "bg-orange-500";
      case STATUS_LIST[5]: // "out_for_delivery"
        return "bg-cyan-500";
      case STATUS_LIST[6]: // "attempted"
        return "bg-teal-500";
      case STATUS_LIST[7]: // "missed"
        return "bg-lime-500";
      case STATUS_LIST[8]: // "postponed"
        return "bg-emerald-500";
      case STATUS_LIST[9]: // "delivered"
        return "bg-yellow-500";
      case STATUS_LIST[10]: // "signed"
        return "bg-green-500";
      case STATUS_LIST[11]: // "exception"
        return "bg-amber-500";
      case STATUS_LIST[12]: // "return_collection"
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className="flex">
      <div className="flex flex-col items-center mr-4">
        <div 
          className={`w-4 h-4 rounded-full ${getStatusIcon(event.status)} ${
            isFirst ? "ring-2 ring-offset-2 ring-blue-300" : ""
          }`}
        />
        {!isLast && <div className="w-0.5 h-full bg-gray-200 mt-2" />}
      </div>
      <div className={`pb-8 ${isLast ? "" : "border-l-2 border-transparent"}`}>
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm text-gray-500">{formatDateTime(event.time)}</p>
          <StatusBadge status={event.status} />
        </div>
        <h3 className="text-lg font-semibold mb-1">{event.actionName}</h3>
        <p className="text-gray-600">{event.note || event.msgFre || event.msgEng}</p>
        
        {/* Enhanced information display */}
        <div className="mt-2 space-y-1">
          {event.location && (
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <span>📍</span>
              <span>{event.location}</span>
            </p>
          )}
          
          {event.user && event.user !== "speedaf" && (
            <p className="text-xs text-blue-600 flex items-center gap-1">
              <span>👤</span>
              <span>{t("packages.updatedBy")}: {event.user}</span>
            </p>
          )}

          {/* Show customer info for delivery-related events */}
          {(event.status === STATUS_LIST[9] || event.status === STATUS_LIST[10]) && customerInfo && (
            <div className="bg-blue-50 rounded p-2 mt-2">
              <p className="text-xs text-blue-700 font-medium">
                {t("packages.customerInfo")}:
              </p>
              <p className="text-xs text-blue-600">
                {customerInfo.name} • {customerInfo.phone}
              </p>
            </div>
          )}
        </div>
        
        {event.pictureUrl && (
          <img
            src="/api/placeholder/200/150"
            alt="Delivery confirmation"
            className="mt-2 rounded-lg"
          />
        )}
      </div>
    </div>
  );
};

const TrackingTimeline = () => {
  const { t } = useTranslation();
  const [trackingNumber, setTrackingNumber] = useState("SP-020061930250");
  const [expanded, setExpanded] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["tracking", trackingNumber],
    queryFn: () => packagesService.packagesTracking(trackingNumber),
    enabled: isSearching, // Only fetch when search is triggered
    retry: 1,
  });

  const handleSearch = () => {
    if (trackingNumber.trim()) {
      setIsSearching(true);
      refetch();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const trackingEvents = data?.data?.[0]?.tracks || [];
  const packageInfo = data?.packageInfo;
  const processedEvents = generateStatusTimeline(trackingEvents);
  const visibleEvents = expanded
    ? processedEvents
    : processedEvents.slice(0, 5);

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-6 h-6" />
          {t("packages.packageTracking")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-6">
          <Input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t("packages.enterTrackingNumber")}
            className="flex-1"
          />
          <Button onClick={handleSearch} disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("packages.loading")}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                {t("packages.track")}
              </span>
            )}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : t("packages.trackingFetchFailed")}
            </AlertDescription>
          </Alert>
        )}

        {/* Customer Information Section */}
        {packageInfo?.customer && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {t("packages.customerInformation")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("packages.customerName")}:
                  </span>
                  <span className="text-sm text-gray-800 capitalize">
                    {packageInfo.customer.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("packages.customerPhone")}:
                  </span>
                  <span className="text-sm text-gray-800">
                    {packageInfo.customer.phone}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("packages.customerCity")}:
                  </span>
                  <span className="text-sm text-gray-800 capitalize">
                    {packageInfo.customer.city}
                  </span>
                </div>
                {packageInfo.customer.secteur && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">
                      {t("packages.customerSecteur")}:
                    </span>
                    <span className="text-sm text-gray-800 capitalize">
                      {packageInfo.customer.secteur}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("packages.totalAmount")}:
                  </span>
                  <span className="text-sm font-semibold text-green-600">
                    {packageInfo.totalAmount} MAD
                  </span>
                </div>
              </div>
            </div>
            {packageInfo.customer.address && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-start gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("packages.customerAddress")}:
                  </span>
                  <span className="text-sm text-gray-800 capitalize">
                    {packageInfo.customer.address}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <ScrollArea className="h-[500px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : processedEvents.length > 0 ? (
            <div className="space-y-6">
              {visibleEvents.map((event, index) => (
                <TrackingEvent
                  key={`${event.time}-${index}`}
                  event={event}
                  isLast={index === visibleEvents.length - 1}
                  isFirst={index === 0}
                  customerInfo={packageInfo?.customer}
                />
              ))}
            </div>
          ) : isSearching ? (
            <div className="text-center py-8 text-gray-500">
              {t("packages.noTrackingFound")}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {t("packages.enterTrackingPrompt")}
            </div>
          )}
        </ScrollArea>

        {processedEvents.length > 5 && (
          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                {t("packages.showLess")}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                {t("packages.showMore")} ({processedEvents.length - 5} more
                events)
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default TrackingTimeline;
